/**
 * @file useCatalogCategories.js
 * @brief Manages category tree loading, editing, search, and pagination for the catalog admin UI.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { FIELD_LIMITS } from "../constants/fieldLimits";

import {
    fetchPaginated,
    flattenLoadedCategories,
    formatCategoryOptionLabel,
} from "../components/CatalogAdmin/utils";


/**
 * Maintain category administration state, tree expansion, and CRUD actions.
 * @param {Object} params Hook parameters.
 * @param {Object} params.api API client used for category requests.
 * @param {Function} params.t Translation helper.
 * @returns {Object} Category tree state, form state, pagination, and CRUD handlers.
 */
export default function useCatalogCategories({ api, t }) {
    const [roots, setRoots] = useState([]);
    const [rootCount, setRootCount] = useState(0);
    const [rootPage, setRootPage] = useState(1);
    const [rootNext, setRootNext] = useState(null);
    const [rootPrevious, setRootPrevious] = useState(null);
    const [rootSearch, setRootSearch] = useState("");
    const [childrenByParent, setChildrenByParent] = useState({});
    const [expandedIds, setExpandedIds] = useState(new Set());
    const [loadingRoots, setLoadingRoots] = useState(true);
    const [loadingIds, setLoadingIds] = useState(new Set());
    const [name, setName] = useState("");
    const [slug, setSlug] = useState("");
    const [parentSearch, setParentSearch] = useState("");
    const [parentCandidates, setParentCandidates] = useState([]);
    const [selectedParent, setSelectedParent] = useState(null);
    const [parentLookupLoading, setParentLookupLoading] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [editDraft, setEditDraft] = useState({ name: "", slug: "", parent: "" });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const parentOptions = useMemo(() => flattenLoadedCategories(roots, childrenByParent), [roots, childrenByParent]);
    const categoryById = useMemo(
        () => parentOptions.reduce((acc, category) => ({ ...acc, [category.id]: category }), {}),
        [parentOptions]
    );

    const loadRoots = useCallback(async (targetPage = 1, targetSearch = "") => {
        setLoadingRoots(true);
        setError(null);
        setChildrenByParent({});
        setExpandedIds(new Set());
        try {
            const normalizedSearch = targetSearch.trim();
            const payload = await fetchPaginated(api, "categories/", {
                page: targetPage,
                parent: normalizedSearch ? undefined : "root",
                search: normalizedSearch || undefined,
            });
            setRoots(payload.items);
            setRootCount(payload.count);
            setRootNext(payload.next);
            setRootPrevious(payload.previous);
            setRootPage(targetPage);
        } catch (requestError) {
            console.error(requestError);
            setError(t("catalogAdmin.loadFailed"));
        } finally {
            setLoadingRoots(false);
        }
    }, [api, t]);

    const loadChildren = useCallback(async (parentIdToLoad, nextPage = 1, append = false) => {
        setLoadingIds((current) => new Set(current).add(parentIdToLoad));
        setError(null);
        try {
            const payload = await fetchPaginated(api, "categories/", {
                page: nextPage,
                parent: parentIdToLoad,
            });

            setChildrenByParent((current) => {
                const existing = current[parentIdToLoad] || { items: [], next: null, previous: null, loaded: false, page: 1 };
                const items = append ? [...existing.items, ...payload.items] : payload.items;
                return {
                    ...current,
                    [parentIdToLoad]: {
                        items,
                        next: payload.next,
                        previous: payload.previous,
                        loaded: true,
                        page: nextPage,
                    },
                };
            });
        } catch (requestError) {
            console.error(requestError);
            setError(t("catalogAdmin.loadChildrenFailed"));
        } finally {
            setLoadingIds((current) => {
                const next = new Set(current);
                next.delete(parentIdToLoad);
                return next;
            });
        }
    }, [api, t]);

    useEffect(() => {
        loadRoots(1, "");
    }, [loadRoots]);

    useEffect(() => {
        const searchTerm = parentSearch.trim();

        if (!searchTerm) {
            if (selectedParent) {
                setParentCandidates([selectedParent]);
            } else {
                setParentCandidates([]);
            }
            setParentLookupLoading(false);
            return;
        }

        let active = true;
        const timer = setTimeout(async () => {
            setParentLookupLoading(true);
            try {
                const payload = await fetchPaginated(api, "categories/", { search: searchTerm, page: 1 });
                if (!active) {
                    return;
                }
                setParentCandidates(payload.items.slice(0, 12));
            } catch (requestError) {
                console.error(requestError);
                if (active) {
                    setParentCandidates([]);
                }
            } finally {
                if (active) {
                    setParentLookupLoading(false);
                }
            }
        }, 260);

        return () => {
            active = false;
            clearTimeout(timer);
        };
    }, [api, parentSearch, selectedParent]);

    const handleToggle = useCallback(async (node) => {
        const nextExpanded = new Set(expandedIds);
        if (nextExpanded.has(node.id)) {
            nextExpanded.delete(node.id);
            setExpandedIds(nextExpanded);
            return;
        }

        nextExpanded.add(node.id);
        setExpandedIds(nextExpanded);

        if ((node.child_count ?? 0) > 0 && !childrenByParent[node.id]?.loaded) {
            await loadChildren(node.id, 1, false);
        }
    }, [childrenByParent, expandedIds, loadChildren]);

    const handleAdd = useCallback(async (event) => {
        event.preventDefault();
        if (!name.trim() || !slug.trim()) {
            return;
        }
        if (name.trim().length > FIELD_LIMITS.categoryName) {
            setError(t("catalogAdmin.categoryNameTooLong", { max: FIELD_LIMITS.categoryName }));
            return;
        }
        if (slug.trim().length > FIELD_LIMITS.categorySlug) {
            setError(t("catalogAdmin.categorySlugTooLong", { max: FIELD_LIMITS.categorySlug }));
            return;
        }

        setSaving(true);
        setError(null);
        try {
            await api.post("categories/", {
                name: name.trim(),
                slug: slug.trim(),
                parent: selectedParent?.id || null,
            });
            setName("");
            setSlug("");
            setParentSearch("");
            setSelectedParent(null);
            setParentCandidates([]);
            setChildrenByParent({});
            setExpandedIds(new Set());
            await loadRoots(1, rootSearch);
        } catch (requestError) {
            const payload = requestError.response?.data;
            setError(payload?.name?.[0] ?? payload?.slug?.[0] ?? t("catalogAdmin.addCategoryFailed"));
        } finally {
            setSaving(false);
        }
    }, [api, loadRoots, name, rootSearch, selectedParent, slug, t]);

    const handleSave = useCallback(async (categoryId) => {
        if (!editDraft.name.trim() || !editDraft.slug.trim()) {
            return;
        }
        if (editDraft.name.trim().length > FIELD_LIMITS.categoryName) {
            setError(t("catalogAdmin.categoryNameTooLong", { max: FIELD_LIMITS.categoryName }));
            return;
        }
        if (editDraft.slug.trim().length > FIELD_LIMITS.categorySlug) {
            setError(t("catalogAdmin.categorySlugTooLong", { max: FIELD_LIMITS.categorySlug }));
            return;
        }

        setSaving(true);
        setError(null);
        try {
            await api.patch(`categories/${categoryId}/`, {
                name: editDraft.name.trim(),
                slug: editDraft.slug.trim(),
                parent: editDraft.parent || null,
            });
            setEditingId(null);
            setChildrenByParent({});
            setExpandedIds(new Set());
            await loadRoots(rootPage, rootSearch);
        } catch {
            setError(t("catalogAdmin.updateFailed"));
        } finally {
            setSaving(false);
        }
    }, [api, editDraft, loadRoots, rootPage, rootSearch, t]);

    const handleDelete = useCallback(async (categoryId) => {
        if (!window.confirm(t("catalogAdmin.deleteCategoryConfirm"))) {
            return;
        }

        setError(null);
        try {
            await api.delete(`categories/${categoryId}/`);
            setChildrenByParent({});
            setExpandedIds(new Set());
            await loadRoots(rootPage, rootSearch);
        } catch {
            setError(t("catalogAdmin.deleteFailed"));
        }
    }, [api, loadRoots, rootPage, rootSearch, t]);

    const clearParentSelection = useCallback(() => {
        setSelectedParent(null);
        setParentSearch("");
        setParentCandidates([]);
    }, []);

    const selectParentCandidate = useCallback((candidate) => {
        setSelectedParent(candidate);
        setParentSearch(formatCategoryOptionLabel(candidate));
    }, []);

    const startEdit = useCallback((category) => {
        setEditingId(category.id);
        setEditDraft({
            name: category.name,
            slug: category.slug,
            parent: category.parent || "",
        });
    }, []);

    return {
        roots,
        rootCount,
        rootPage,
        rootNext,
        rootPrevious,
        rootSearch,
        setRootSearch,
        childrenByParent,
        expandedIds,
        loadingRoots,
        loadingIds,
        name,
        setName,
        slug,
        setSlug,
        parentSearch,
        setParentSearch,
        parentCandidates,
        selectedParent,
        setSelectedParent,
        parentLookupLoading,
        editingId,
        setEditingId,
        editDraft,
        setEditDraft,
        saving,
        error,
        parentOptions,
        categoryById,
        loadRoots,
        loadChildren,
        handleToggle,
        handleAdd,
        handleSave,
        handleDelete,
        clearParentSelection,
        selectParentCandidate,
        startEdit,
    };
}
